import { ComparatorReturns, ListStore } from '@list-store';
import { debounce, DebouncedFunc, some } from 'lodash';
import {
    DispositionView,
    ManageUnitsEventView,
    ManageUnitsResource,
    ManageUnitsUnitGroupEnumType,
    ManageUnitsUnitView,
    ManageUnitsUpdateForm,
    ResponsePlanV2,
    UnitFiltersForm,
} from '@mark43/cad-api';
import { TileUnit } from '../common/types';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient, createApiErrorHandler } from '@m43/core';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { InlineBannerProps, modals, Box } from '@cad-arc';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useDispatchAreaStore } from '../dispatch-areas/use-dispatch-areas';
import { useMutation } from '@tanstack/react-query';
import { showAssignAndHoldConfirmationModal } from '../common/show-assign-and-hold-confirmation-modal';
import { createSortComparator } from './common/create-sort-comparator';
import { sortingConfig, SortingParameterEnum, SortingParameterName } from './common/sorting-config';
import { useIntegratedManageUnitsStore } from './use-integrated-manage-units-store';
import { globalAttributes } from '../common/global-attributes';
import { useAttributesByType } from '../attributes/use-attributes-by-type';
import { tokenize } from './common/tokenize';
import { SortDirection } from '../common/sorting/types';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { groupUnitTypeIdsByClass } from '../common/helpers/unit-types';

export interface ManageUnitsBanner {
    status: InlineBannerProps['status'];
    title?: ReactNode;
    description: ReactNode;
}

interface UseManageUnitsDrawerDataReturnType {
    banners: ManageUnitsBanner[];
    store: ListStore<ManageUnitsUnitView>;
    event?: ManageUnitsEventView;
    debouncedSetQuery: DebouncedFunc<React.Dispatch<string>>;
    sortDirection: SortDirection;
    setSortDirection: React.Dispatch<SortDirection>;
    sortingParam: SortingParameterName;
    setSortingParam: React.Dispatch<SortingParameterName>;
    selectedFilters: UnitFiltersForm;
    setSelectedFilters: React.Dispatch<UnitFiltersForm>;
    hasSelectedFilters: boolean;
    toggleSelectUnit: (unit: ManageUnitsUnitView | TileUnit) => void;
    primaryUnitId?: number;
    initiallyDispatchedUnitIds: number[];
    selectedUnitIds: number[];
    releasedUnitIds: number[];
    responsePlan?: ResponsePlanV2;
    primaryDispositionInfo?: DispositionView;
    setPrimaryUnitIdAndSelectIfNecessary: (unit: ManageUnitsUnitView | TileUnit) => void;
    submit: ({
        confirmationAcknowledged,
        assignAndHold,
    }: {
        confirmationAcknowledged?: boolean;
        assignAndHold?: boolean;
    }) => void;
    isEventQueryLoading: boolean;
    isUnitsQueryLoading: boolean;
    isUnitsQueryError: boolean;
    areUnitsFetchedAfterMount: boolean;
    isMutationLoading: boolean;
}

interface RecommendUnitSortValue {
    manageUnitGroup: ManageUnitsUnitGroupEnumType | undefined;
    sortKey: string | undefined;
    secondarySortValue: string | number | undefined;
}

export function useManageUnitsDrawerData(eventId: number): UseManageUnitsDrawerDataReturnType {
    const drawerControls = useDrawerControls();
    const { automaticallyHoldEventsForUnits, unitLevelDispositionsEnabled } = useEnabledFeatures();

    const { attributes: unitTypeAttributes } = useAttributesByType({ attributeType: 'UNIT_TYPE' });

    const [banners, setBanners] = useState<ManageUnitsBanner[]>([]);

    const dispatchAreaStore = useDispatchAreaStore();
    const [primaryUnitId, setPrimaryUnitId] = useState<number>();
    const primaryUnitIdRef = useRef(primaryUnitId);
    primaryUnitIdRef.current = primaryUnitId;

    const defaultFilters: UnitFiltersForm = useMemo(() => {
        return {
            dispatchAreaIds: dispatchAreaStore.monitoredAndOwnedDispatchAreaIds,
            policeUnitTypeIds: [],
            fireUnitTypeIds: [],
            emsUnitTypeIds: [],
            skillIds: [],
            equipmentIds: [],
        };
    }, [dispatchAreaStore.monitoredAndOwnedDispatchAreaIds]);

    const [selectedFilters, setSelectedFilters] = useState<UnitFiltersForm>(defaultFilters);

    const hasSelectedFilters = useMemo(() => {
        for (const filters in Object.values(selectedFilters)) {
            if (filters.length > 0) {
                return true;
            }
        }
        return false;
    }, [selectedFilters]);

    const {
        store,
        manageUnitsEventResponse,
        manageUnitsUnitsResponse,
        isEventQueryLoading,
        isUnitsQueryLoading,
        isUnitsQueryError,
        selectedUnitIds,
        toggleUnitSelection,
        releasedUnitIds,
        areUnitsFetchedAfterMount,
    } = useIntegratedManageUnitsStore({
        eventId,
        defaultFilters,
        selectedFilters,
        unitTypeAttributes,
    });
    const primaryDispositionInfo = manageUnitsEventResponse?.primaryDisposition;
    const releasedPrimaryUnitId = primaryDispositionInfo?.isPrimary
        ? primaryDispositionInfo?.unitId
        : undefined;
    const isPrimaryUnitRequired =
        !unitLevelDispositionsEnabled || (unitLevelDispositionsEnabled && !releasedPrimaryUnitId);

    const [hasSetInitialFilters, setHasSetInitialFilters] = useState(false);
    const setInitialFilters = useCallback(
        (filters: UnitFiltersForm) => {
            if (!hasSetInitialFilters) {
                setSelectedFilters(filters);
                setHasSetInitialFilters(true);
            }
        },
        [hasSetInitialFilters, setHasSetInitialFilters, setSelectedFilters]
    );

    useEffect(() => {
        if (manageUnitsUnitsResponse) {
            if (manageUnitsUnitsResponse.units) {
                if (
                    unitLevelDispositionsEnabled &&
                    releasedPrimaryUnitId &&
                    manageUnitsUnitsResponse.initialPrimaryUnitId === releasedPrimaryUnitId
                ) {
                    // The released (dispositioned) unit is still returned as the initial primary —
                    // don't preselect it. Guard: only clear if the dispatcher hasn't already
                    // manually picked a different primary (e.g. on a refetch triggered by a filter
                    // change or socket reconnect), so an in-progress selection isn't silently wiped.
                    const hasManuallySelectedPrimary =
                        primaryUnitIdRef.current !== undefined &&
                        primaryUnitIdRef.current !== releasedPrimaryUnitId;
                    if (!hasManuallySelectedPrimary) {
                        setPrimaryUnitId(undefined);
                    }
                } else {
                    // Either no released primary, or a genuinely new active primary exists (different
                    // from the released unit) — reflect the backend state in the UI.
                    setPrimaryUnitId(manageUnitsUnitsResponse.initialPrimaryUnitId);
                }
            }

            if (manageUnitsUnitsResponse.responsePlanV2) {
                if (!manageUnitsUnitsResponse.responsePlanV2.isComplete) {
                    setBanners([
                        {
                            status: 'attention',
                            title: 'Missing Recommended Units',
                            description: manageUnitsUnitsResponse.responsePlanV2.message,
                        },
                    ]);
                }
            }
        }
    }, [
        manageUnitsUnitsResponse,
        isPrimaryUnitRequired,
        unitLevelDispositionsEnabled,
        releasedPrimaryUnitId,
    ]);

    useEffect(() => {
        if (
            hasSetInitialFilters ||
            !unitTypeAttributes?.length ||
            !manageUnitsEventResponse?.event
        ) {
            return;
        }

        const { policeUnitTypeIds, fireUnitTypeIds, emsUnitTypeIds } = groupUnitTypeIdsByClass(
            unitTypeAttributes
        );

        const { agencyTypeGlobalAttrId } = manageUnitsEventResponse.event;
        const isPoliceEvent = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;
        const isFireEvent = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.FIRE;
        const isEmsEvent = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.EMS;

        setInitialFilters({
            ...defaultFilters,
            policeUnitTypeIds: isPoliceEvent ? policeUnitTypeIds : [],
            fireUnitTypeIds: isFireEvent ? fireUnitTypeIds : [],
            emsUnitTypeIds: isEmsEvent ? emsUnitTypeIds : [],
        });
    }, [
        manageUnitsEventResponse,
        defaultFilters,
        selectedFilters,
        hasSetInitialFilters,
        setInitialFilters,
        unitTypeAttributes,
    ]);

    const initiallySelectedUnits = useMemo(
        () => manageUnitsUnitsResponse?.initiallySelectedUnits || [],
        [manageUnitsUnitsResponse]
    );

    const initiallySelectedUnitIds = useMemo(() => {
        return initiallySelectedUnits.map((u) => u.id);
    }, [initiallySelectedUnits]);

    const initiallyDispatchedUnits = useMemo(
        () =>
            initiallySelectedUnits.filter(
                (unit) => !['HELD', 'RECOMMENDED'].includes(unit.manageUnitGroup)
            ),
        [initiallySelectedUnits]
    );

    const initiallyDispatchedUnitIds = useMemo(() => {
        return initiallyDispatchedUnits.map((u) => u.id);
    }, [initiallyDispatchedUnits]);

    const [query, setQuery] = useState<string>('');
    const [sortingParam, setSortingParam] = useState<SortingParameterName>(
        SortingParameterEnum.DEFAULT
    );
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const apiClient = useApiClient();

    const debouncedSetQuery = React.useMemo(() => debounce(setQuery, 100), [setQuery]);

    useEffect(() => {
        store.setFilterDirective((unit) => {
            return (
                selectedUnitIds.includes(unit.id) ||
                initiallySelectedUnitIds.includes(unit.id) ||
                (query ? some(tokenize(query), (token) => unit.searchValue.includes(token)) : true)
            );
        });
    }, [store, query, selectedUnitIds, initiallySelectedUnitIds]);

    useEffect(() => {
        const sortComparator = createSortComparator(
            sortDirection,
            sortingConfig[sortingParam].sortType
        );
        const manageUnitSortComparatorByUnitGroup = (
            left: RecommendUnitSortValue,
            right: RecommendUnitSortValue
        ) => {
            if (left.manageUnitGroup === 'RECOMMENDED' && right.manageUnitGroup === 'RECOMMENDED') {
                // RECOMMENDED group only cares about 'sortKey', which is a BE sort priory value for response plan,
                // in this group, ignore current SortBy and Direction value, always sort by 'sortKey' in Ascending order
                return (left.sortKey ?? '').localeCompare(right.sortKey ?? '') as ComparatorReturns;
            }
            // for other groups, sort by secondarySortValue (sortingConfig[sortingParam].getSortValue(item))
            return sortComparator(left.secondarySortValue, right.secondarySortValue);
        };

        store.setSortingDirective({
            getSortValue: (item): RecommendUnitSortValue => {
                // return a custom object for comparator. we will perform different sort logic based on 'manageUnitGroup'
                return {
                    manageUnitGroup: item.manageUnitGroup,
                    sortKey: item.sortKey,
                    secondarySortValue: sortingConfig[sortingParam].getSortValue(item),
                };
            },
            comparator: manageUnitSortComparatorByUnitGroup,
        });
    }, [store, sortDirection, sortingParam]);

    const toggleSelectUnit = useCallback(
        (unit: ManageUnitsUnitView | TileUnit) => {
            setBanners([]);
            toggleUnitSelection(unit.id);
            if (isPrimaryUnitRequired) {
                if (!primaryUnitId && !selectedUnitIds.length) {
                    setPrimaryUnitId(unit.id);
                } else if (primaryUnitId === unit.id) {
                    setPrimaryUnitId(undefined);
                }
            } else if (
                unitLevelDispositionsEnabled &&
                releasedPrimaryUnitId &&
                primaryUnitId === unit.id
            ) {
                setPrimaryUnitId(undefined);
            }
        },
        [
            primaryUnitId,
            selectedUnitIds.length,
            toggleUnitSelection,
            isPrimaryUnitRequired,
            unitLevelDispositionsEnabled,
            releasedPrimaryUnitId,
        ]
    );

    const setPrimaryUnitIdAndSelectIfNecessary = useCallback(
        (unit: ManageUnitsUnitView | TileUnit) => {
            if (unit.id !== primaryUnitId && !selectedUnitIds.includes(unit.id)) {
                toggleSelectUnit(unit);
            }
            if (unit.id === primaryUnitId) {
                setPrimaryUnitId(undefined);
            } else {
                setPrimaryUnitId(unit.id);
            }
        },
        [primaryUnitId, setPrimaryUnitId, selectedUnitIds, toggleSelectUnit]
    );

    const validate = useCallback(
        (input: ManageUnitsUpdateForm): ManageUnitsBanner[] => {
            const banners: ManageUnitsBanner[] = [];

            if (!input.unitIds.length) {
                banners.push({
                    status: 'error',
                    title: 'No units selected',
                    description: 'Select a unit to be dispatched',
                });
            }

            const noPrimaryUnitIsSelected =
                typeof input.primaryUnitId === 'undefined' ||
                !selectedUnitIds.includes(input.primaryUnitId);

            if (isPrimaryUnitRequired && noPrimaryUnitIsSelected) {
                banners.push({
                    status: 'error',
                    title: 'No unit assigned as primary',
                    description: 'Select one of the selected units as primary',
                });
            }

            return banners;
        },
        [selectedUnitIds, isPrimaryUnitRequired]
    );

    const mutation = useMutation((input: ManageUnitsUpdateForm) => {
        return apiClient.request<ManageUnitsResource.Update>({
            method: 'POST',
            url: 'manage_units/',
            data: input,
        });
    });

    const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

    // When you submit the manage units drawer, there is a chance you are assigning units in a way that will leave
    // a currently assigned event without any responding units. So, we submit this request and account for the
    // fact that the API may return a response asking for the user to confirm the changes.
    //
    // The first call to "submit" defaults confirmationAcknowledged and assignAndHold to false. In the subsequent call, we set
    // these values based on the user's selection in the Confirmation Modal. Once we receive a response that indicates
    // a successful request without any confirmation necessary, we close both the modal and the drawer.
    const submit = useCallback(
        (
            {
                confirmationAcknowledged,
                assignAndHold,
            }: {
                confirmationAcknowledged?: boolean;
                assignAndHold?: boolean;
            } = { confirmationAcknowledged: false, assignAndHold: false }
        ) => {
            const input: ManageUnitsUpdateForm = {
                eventId,
                unitIds: selectedUnitIds,
                primaryUnitId,
                confirmationAcknowledged,
                assignAndHold,
            };

            const validationErrors = validate(input);

            if (validationErrors.length) {
                setBanners(validationErrors);
            } else {
                mutation
                    .mutateAsync({ ...input, confirmationAcknowledged, assignAndHold })
                    .then((resp) => {
                        if (resp.data.data?.confirmationMessage) {
                            const body = (
                                <Box whiteSpace="pre-line">
                                    {resp.data.data.confirmationMessage}
                                </Box>
                            );

                            if (resp.data.data.showAssignAndHoldConfirmationOption) {
                                showAssignAndHoldConfirmationModal({
                                    body,
                                    onAssignAndHold: () =>
                                        submit({
                                            confirmationAcknowledged: true,
                                            assignAndHold: true,
                                        }),
                                    onAssignOnly: () =>
                                        submit({
                                            confirmationAcknowledged: true,
                                            assignAndHold: false,
                                        }),
                                    cancelButtonRef,
                                });
                            } else {
                                modals.confirm({
                                    title: 'Assign Units',
                                    body,
                                    onConfirm: () => {
                                        submit({
                                            confirmationAcknowledged: true,
                                            assignAndHold: automaticallyHoldEventsForUnits,
                                        });
                                    },
                                });
                            }
                        } else if (resp?.data.success) {
                            drawerControls.close();
                        }
                    })
                    .catch(
                        createApiErrorHandler(() => {
                            setBanners([
                                {
                                    status: 'error',
                                    title: 'Error',
                                    description: errorOccurredWhile('assigning units'),
                                },
                            ]);
                        })
                    );
            }
        },
        [
            mutation,
            drawerControls,
            eventId,
            selectedUnitIds,
            primaryUnitId,
            validate,
            setBanners,
            automaticallyHoldEventsForUnits,
        ]
    );

    return {
        store,
        banners,
        event: manageUnitsEventResponse?.event,
        debouncedSetQuery,
        sortDirection,
        setSortDirection,
        sortingParam,
        setSortingParam,
        selectedFilters,
        setSelectedFilters,
        hasSelectedFilters,
        initiallyDispatchedUnitIds,
        selectedUnitIds,
        toggleSelectUnit,
        releasedUnitIds,
        primaryUnitId,
        primaryDispositionInfo,
        setPrimaryUnitIdAndSelectIfNecessary,
        submit,
        responsePlan: manageUnitsUnitsResponse?.responsePlanV2,
        isEventQueryLoading,
        isUnitsQueryLoading,
        isUnitsQueryError,
        areUnitsFetchedAfterMount,
        isMutationLoading: mutation.isLoading,
    } as const;
}
